import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronDown, ChevronRight, Info, Pencil, Plus, Trash2, TrendingUp, Users, User } from "lucide-react";

import PilotageEmptyState from "@/components/pilotage/PilotageEmptyState";
import PilotageField from "@/components/pilotage/PilotageField";
import PilotageModal from "@/components/pilotage/PilotageModal";
import PilotageTable from "@/components/pilotage/PilotageTable";
import AppErrorState from "@/components/ui/AppErrorState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage } from "@/lib/api";
import {
  COLLABORATOR_STATUSES,
  CONTRACT_KINDS,
  createCollaborator,
  deleteCollaborator,
  listCollaborators,
  updateCollaborator,
} from "@/lib/pilotageApi";
import {
  calculateSeniority,
  getResourceTypeLabel,
  getContractKindLabel,
  getCollaboratorStatusLabel,
  getOperationalStatusLabel,
  getOperationalStatusBadgeClass,
  formatDateLabel,
  formatEUR0,
  formatEUR2,
} from "@/lib/pilotageUi";

const COLLABORATOR_STATUS_FILTERS = [
  { value: "all", label: "Tous" },
  { value: "ACTIVE", label: "Actif" },
  { value: "NOTICE", label: "Préavis" },
  { value: "PLANNED", label: "Planifié" },
  { value: "LEFT", label: "Sorti" },
  { value: "INACTIVE", label: "Inactif" },
  { value: "RECRUITMENT", label: "Recrutement" },
];

const normalizeSearchText = (value) =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

const toInputDate = (value) => {
  if (!value) {
    return "";
  }

  return String(value).slice(0, 10);
};

const EMPTY_FORM = {
  full_name: "",
  email: "",
  status: "ACTIVE",
  contract_kind: "FREELANCE",
  internal_cost_monthly: "",
  annual_salary: "",
  employer_cost_coefficient: "1.5",
  salary_effective_date: "",
  salary_changes: [],
  employment_start_date: "",
  employment_end_date: "",
};

const CollaboratorsPage = () => {
  const { role } = useAuth();
  const isReadOnly = role === "RH";

  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCollaborator, setEditingCollaborator] = useState(null);
  const [formValues, setFormValues] = useState(EMPTY_FORM);
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    contractKind: "all",
  });
  const [isSalaryChangesOpen, setIsSalaryChangesOpen] = useState(false);
  const [editingChangeIndex, setEditingChangeIndex] = useState(null);
  const [evolutionForm, setEvolutionForm] = useState({
    effective_date: "",
    value: "",
    comment: "",
  });
  const [evolutionError, setEvolutionError] = useState("");
  const todayStr = useMemo(() => new Date().toISOString().substring(0, 10), []);
  const showEmptyState = !loading && collaborators.length === 0;

  const loadCollaborators = async () => {
    setLoading(true);
    setError("");

    try {
      const data = await listCollaborators();
      setCollaborators(data);
    } catch (requestError) {
      setError(
        getApiErrorMessage(requestError, "Impossible de charger les collaborateurs."),
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadCollaborators();
  }, []);

  const contractKindOptions = useMemo(() => {
    const contractKinds = [
      ...CONTRACT_KINDS,
      ...collaborators.map((collaborator) => collaborator?.contract_kind).filter(Boolean),
    ];

    return Array.from(new Set(contractKinds)).map((contractKind) => ({
      value: contractKind,
      label: getContractKindLabel(contractKind),
    }));
  }, [collaborators]);

  const filteredCollaborators = useMemo(() => {
    const query = normalizeSearchText(filters.search);

    return collaborators.filter((collaborator) => {
      const status = String(collaborator.status || "").toUpperCase();
      const contractKind = String(collaborator.contract_kind || "").toUpperCase();

      if (filters.status !== "all" && status !== filters.status) {
        return false;
      }
      if (filters.contractKind !== "all" && contractKind !== filters.contractKind) {
        return false;
      }
      if (!query) {
        return true;
      }

      const searchValues = [
        collaborator.full_name,
        collaborator.email,
        collaborator.status,
        getCollaboratorStatusLabel(collaborator.status),
        collaborator.contract_kind,
        getContractKindLabel(collaborator.contract_kind),
        collaborator.operational_status,
        collaborator.operational_status_label,
        getOperationalStatusLabel(collaborator.operational_status),
      ];

      return normalizeSearchText(searchValues.join(" ")).includes(query);
    });
  }, [collaborators, filters]);

  const resetFilters = () => {
    setFilters({
      search: "",
      status: "all",
      contractKind: "all",
    });
  };

  const openCreateModal = () => {
    setEditingCollaborator(null);
    setFormValues(EMPTY_FORM);
    setIsSalaryChangesOpen(false);
    setEditingChangeIndex(null);
    setEvolutionError("");
    setModalOpen(true);
  };

  const openEditModal = (collaborator) => {
    setEditingCollaborator(collaborator);
    setFormValues({
      full_name: collaborator.full_name || "",
      email: collaborator.email || "",
      status: collaborator.status || "ACTIVE",
      contract_kind: collaborator.contract_kind || "FREELANCE",
      internal_cost_monthly: collaborator.internal_cost_monthly !== null && collaborator.internal_cost_monthly !== undefined ? String(collaborator.internal_cost_monthly) : "",
      annual_salary: collaborator.annual_salary !== null && collaborator.annual_salary !== undefined ? String(collaborator.annual_salary) : "",
      employer_cost_coefficient: collaborator.employer_cost_coefficient !== null && collaborator.employer_cost_coefficient !== undefined ? String(collaborator.employer_cost_coefficient) : "1.5",
      salary_effective_date: toInputDate(collaborator.salary_effective_date || collaborator.employment_start_date),
      salary_changes: Array.isArray(collaborator.salary_changes) ? collaborator.salary_changes : [],
      employment_start_date: toInputDate(collaborator.employment_start_date),
      employment_end_date: toInputDate(collaborator.employment_end_date || collaborator.exit_date),
    });
    setIsSalaryChangesOpen(false);
    setEditingChangeIndex(null);
    setEvolutionError("");
    setModalOpen(true);
  };

  const closeModal = (nextOpen) => {
    setModalOpen(nextOpen);
    if (!nextOpen) {
      setEditingCollaborator(null);
      setFormValues(EMPTY_FORM);
      setIsSalaryChangesOpen(false);
      setEditingChangeIndex(null);
      setEvolutionError("");
    }
  };

  const handleOpenAddEvolution = () => {
    setEditingChangeIndex("new");
    setEvolutionForm({
      effective_date: "",
      value: "",
      comment: "",
    });
    setEvolutionError("");
  };

  const handleEditEvolution = (index) => {
    const item = (formValues.salary_changes || [])[index];
    if (!item) return;
    setEditingChangeIndex(index);
    setEvolutionForm({
      effective_date: toInputDate(item.effective_date),
      value: item.value !== null && item.value !== undefined ? String(item.value) : "",
      comment: item.comment || "",
    });
    setEvolutionError("");
  };

  const handleCancelEvolution = () => {
    setEditingChangeIndex(null);
    setEvolutionForm({ effective_date: "", value: "", comment: "" });
    setEvolutionError("");
  };

  const handleDeleteEvolution = (index) => {
    setFormValues((curr) => {
      const nextChanges = [...(curr.salary_changes || [])];
      nextChanges.splice(index, 1);
      return { ...curr, salary_changes: nextChanges };
    });
  };

  const handleSaveEvolution = (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    setEvolutionError("");

    if (!evolutionForm.effective_date) {
      setEvolutionError("La date d'effet est obligatoire.");
      return;
    }
    const parsedVal = parseFloat(evolutionForm.value);
    if (isNaN(parsedVal) || parsedVal < 0) {
      setEvolutionError("Le nouveau salaire doit être un nombre positif ou nul.");
      return;
    }

    const effDate = evolutionForm.effective_date;

    // Duplicate date check
    const isDuplicate = (formValues.salary_changes || []).some(
      (c, idx) => idx !== editingChangeIndex && toInputDate(c.effective_date) === effDate,
    );
    if (isDuplicate) {
      setEvolutionError(`Une évolution salariale existe déjà à la date du ${formatDateLabel(effDate)}.`);
      return;
    }

    // Check employment_start_date
    if (formValues.employment_start_date && effDate < formValues.employment_start_date) {
      setEvolutionError(
        `La date d'effet (${formatDateLabel(effDate)}) ne peut pas être antérieure à la date d'entrée (${formatDateLabel(formValues.employment_start_date)}).`,
      );
      return;
    }

    // Check employment_end_date
    if (formValues.employment_end_date && effDate > formValues.employment_end_date) {
      setEvolutionError(
        `La date d'effet (${formatDateLabel(effDate)}) ne peut pas être postérieure à la date de sortie (${formatDateLabel(formValues.employment_end_date)}).`,
      );
      return;
    }

    const newChangeObj = {
      id:
        editingChangeIndex !== "new" && formValues.salary_changes?.[editingChangeIndex]?.id
          ? formValues.salary_changes[editingChangeIndex].id
          : `sc-${Date.now()}`,
      effective_date: effDate,
      value: parsedVal,
      comment: evolutionForm.comment ? evolutionForm.comment.trim() : null,
    };

    setFormValues((curr) => {
      let list = [...(curr.salary_changes || [])];
      if (editingChangeIndex === "new") {
        list.push(newChangeObj);
      } else {
        list[editingChangeIndex] = newChangeObj;
      }
      list.sort((a, b) => (toInputDate(a.effective_date) > toInputDate(b.effective_date) ? 1 : -1));
      return { ...curr, salary_changes: list };
    });

    setEditingChangeIndex(null);
    setEvolutionForm({ effective_date: "", value: "", comment: "" });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    const parseOptionalNumber = (val) => {
      if (val === "" || val === null || val === undefined) return null;
      const parsed = parseFloat(val);
      return Number.isFinite(parsed) ? parsed : null;
    };

    const payload = {
      full_name: formValues.full_name.trim(),
      email: formValues.email.trim(),
      status: formValues.status,
      contract_kind: formValues.contract_kind,
      internal_cost_monthly: formValues.internal_cost_monthly !== "" ? parseFloat(formValues.internal_cost_monthly) : null,
      annual_salary: parseOptionalNumber(formValues.annual_salary),
      employer_cost_coefficient: parseOptionalNumber(formValues.employer_cost_coefficient),
      salary_effective_date: formValues.salary_effective_date || formValues.employment_start_date || null,
      salary_changes: Array.isArray(formValues.salary_changes)
        ? formValues.salary_changes.map((c) => ({
            id: c.id,
            effective_date: toInputDate(c.effective_date),
            value: Number(c.value),
            comment: c.comment || null,
          }))
        : [],
      employment_start_date: formValues.employment_start_date || null,
      employment_end_date: formValues.employment_end_date || null,
    };

    try {
      if (editingCollaborator) {
        await updateCollaborator(editingCollaborator.id, payload);
      } else {
        await createCollaborator(payload);
      }

      closeModal(false);
      await loadCollaborators();
    } catch (requestError) {
      if (requestError?.response?.status === 409) {
        import("react-hot-toast").then(({ toast }) => {
          toast.error(
            <div>
              Un collaborateur existe déjà pour cette adresse email.
            </div>,
            { duration: 5000 }
          );
        });
        setError("Un collaborateur existe déjà avec cet email.");
      } else {
        setError(
          getApiErrorMessage(requestError, "Impossible d'enregistrer le collaborateur."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (collaborator) => {
    const shouldDelete = window.confirm(
      `Supprimer le collaborateur "${collaborator.full_name}" ?`,
    );

    if (!shouldDelete) {
      return;
    }

    setError("");

    try {
      await deleteCollaborator(collaborator.id);
      await loadCollaborators();
    } catch (requestError) {
      setError(
        getApiErrorMessage(requestError, "Impossible de supprimer le collaborateur."),
      );
    }
  };

  const isSalariedContract = ["CDI", "CDD", "ALTERNANT"].includes(
    String(formValues.contract_kind || "").toUpperCase(),
  );
  const annualSalaryNum =
    formValues.annual_salary !== "" && !isNaN(Number(formValues.annual_salary))
      ? Math.max(0, Number(formValues.annual_salary))
      : 0;
  const employerCoeffNum =
    formValues.employer_cost_coefficient !== "" &&
    !isNaN(Number(formValues.employer_cost_coefficient))
      ? Math.max(0, Number(formValues.employer_cost_coefficient))
      : 1.5;
  const estimatedAnnualCost = annualSalaryNum * employerCoeffNum;
  const estimatedMonthlyCost = estimatedAnnualCost / 12;
  const calculatedCjm = estimatedAnnualCost / 210;

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Pilotage
            </p>
            <h1 className="mt-2 text-2xl font-heading font-bold text-white">
              Collaborateurs
            </h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Référentiel des consultants. Les RH peuvent consulter les profils,
              tandis que l&apos;administration conserve le CRUD complet.
            </p>
          </div>
          {!isReadOnly && !showEmptyState ? (
            <Button type="button" onClick={openCreateModal} className="btn-primary">
              <Plus className="h-4 w-4" />
              Créer collaborateur
            </Button>
          ) : null}
        </div>
      </section>

      {error ? <AppErrorState message={error} /> : null}

      {!showEmptyState ? (
        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(260px,1.5fr)_repeat(2,minmax(160px,1fr))_auto]">
            <Input
              type="search"
              value={filters.search}
              onChange={(event) =>
                setFilters((current) => ({ ...current, search: event.target.value }))
              }
              placeholder="Rechercher un collaborateur..."
              className="bg-slate-950/80"
            />
            <select
              aria-label="Statut collaborateur"
              value={filters.status}
              onChange={(event) =>
                setFilters((current) => ({ ...current, status: event.target.value }))
              }
              className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
            >
              {COLLABORATOR_STATUS_FILTERS.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
            <select
              aria-label="Type de contrat"
              value={filters.contractKind}
              onChange={(event) =>
                setFilters((current) => ({ ...current, contractKind: event.target.value }))
              }
              className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
            >
              <option value="all">Tous</option>
              {contractKindOptions.map((contractKind) => (
                <option key={contractKind.value} value={contractKind.value}>
                  {contractKind.label}
                </option>
              ))}
            </select>
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={resetFilters}
              className="h-9 whitespace-nowrap border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
            >
              Réinitialiser les filtres
            </Button>
          </div>
          <p className="mt-3 text-xs font-medium text-slate-400">
            {filteredCollaborators.length} collaborateurs affichés / {collaborators.length} au total
          </p>
        </section>
      ) : null}

      {showEmptyState ? (
        <PilotageEmptyState
          icon={Users}
          title="Aucun collaborateur"
          description="Ajoutez un premier collaborateur pour pouvoir l'affecter aux missions de pilotage."
          actionLabel="Créer collaborateur"
          onAction={openCreateModal}
          isReadOnly={isReadOnly}
          testId="collaborators-empty-state"
        />
      ) : (
        <PilotageTable
          columns={[
            {
              key: "full_name",
              label: "Nom",
              render: (collaborator) => (
                <div className="flex items-center gap-2">
                  <Link
                    to={`/admin/pilotage/collaborateurs/${collaborator.id}`}
                    className="block hover:bg-white/5 -mx-2 px-2 py-1 rounded transition-colors group"
                  >
                    <p className="font-medium text-white group-hover:text-lenvia-blue transition-colors">
                      {collaborator.full_name}
                    </p>
                    <p className="mt-1 text-xs text-slate-500 group-hover:text-lenvia-blue/70 transition-colors">
                      {collaborator.id}
                    </p>
                  </Link>
                  {collaborator.candidate_id && (
                    <Link
                      to={`/admin/recrutement/candidats/${collaborator.candidate_id}`}
                      className="text-violet-400 hover:text-violet-300 transition-colors"
                      title="Voir la fiche candidat"
                    >
                      <User className="h-4 w-4" />
                    </Link>
                  )}
                </div>
              ),
            },
            {
              key: "email",
              label: "Email",
              render: (collaborator) => collaborator.email,
            },
            {
              key: "status",
              label: "Statut",
              render: (collaborator) => {
                const status = collaborator.status || "ACTIVE";
                const label = getCollaboratorStatusLabel(status);
                let colorClass = "bg-slate-400/10 text-slate-400 ring-slate-400/20";
                if (status === "ACTIVE") {
                  colorClass = "bg-emerald-400/10 text-emerald-400 ring-emerald-400/20";
                } else if (status === "RECRUITMENT") {
                  colorClass = "bg-blue-400/10 text-blue-400 ring-blue-400/20";
                } else if (status === "PLANNED") {
                  colorClass = "bg-amber-400/10 text-amber-400 ring-amber-400/20";
                } else if (status === "NOTICE") {
                  colorClass = "bg-orange-400/10 text-orange-400 ring-orange-400/20";
                }
                const opStatus = collaborator.operational_status;
                const opLabel = collaborator.operational_status_label || (opStatus ? getOperationalStatusLabel(opStatus) : null);
                return (
                  <div className="flex flex-col gap-1 items-start">
                    <span
                      className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${colorClass}`}
                    >
                      {label}
                    </span>
                    {opLabel ? (
                      <span
                        data-testid="collaborator-row-operational-badge"
                        className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium border ${getOperationalStatusBadgeClass(opStatus)}`}
                      >
                        {opLabel}
                      </span>
                    ) : null}
                  </div>
                );
              },
            },
            {
              key: "contract_kind",
              label: "Contrat",
              render: (collaborator) => {
                const kind = collaborator.contract_kind;
                const resourceLabel = getResourceTypeLabel(kind);
                const kindLabel = getContractKindLabel(kind);
                return (
                  <div className="flex flex-col">
                    <span className="text-white text-sm">{kindLabel}</span>
                    <span className="text-slate-500 text-xs mt-0.5">{resourceLabel}</span>
                  </div>
                );
              },
            },
            {
              key: "employment_start_date",
              label: "Entrée",
              render: (collaborator) => formatDateLabel(collaborator.employment_start_date),
            },
            {
              key: "employment_end_date",
              label: "Sortie",
              render: (collaborator) => {
                const endDate = collaborator.employment_end_date || collaborator.exit_date;
                if (!endDate) {
                  return (
                    <div className="flex items-center gap-1.5 text-slate-400">
                      <span>—</span>
                      {collaborator.status === "ACTIVE" && (
                        <span
                          className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-400"
                          title="Collaborateur actuellement présent"
                        />
                      )}
                    </div>
                  );
                }
                return formatDateLabel(endDate);
              },
            },
            {
              key: "seniority",
              label: "Ancienneté",
              render: (collaborator) =>
                calculateSeniority(
                  collaborator.employment_start_date,
                  collaborator.employment_end_date,
                ),
            },
          ]}
          rows={filteredCollaborators}
          loading={loading}
          emptyMessage="Aucun collaborateur ne correspond aux filtres."
          renderActions={
            isReadOnly
              ? null
              : (collaborator) => (
                  <div className="flex justify-end gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(collaborator)}
                      className="border-white/10 text-slate-200 hover:bg-white/5 hover:text-white"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Modifier
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(collaborator)}
                      className="border-red-500/20 text-red-200 hover:bg-red-500/10 hover:text-red-100"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Supprimer
                    </Button>
                  </div>
                )
          }
        />
      )}

      {!isReadOnly ? (
        <PilotageModal
          open={modalOpen}
          onOpenChange={closeModal}
          title={
            editingCollaborator
              ? "Modifier le collaborateur"
              : "Créer un collaborateur"
          }
          description="Le collaborateur pourra ensuite être affecté à une ou plusieurs missions."
          onSubmit={handleSubmit}
          submitLabel={editingCollaborator ? "Enregistrer" : "Créer"}
          isSubmitting={saving}
        >
          <div className="grid gap-4 md:grid-cols-2">
            <PilotageField label="Nom complet" htmlFor="collaborator-name">
              <Input
                id="collaborator-name"
                value={formValues.full_name}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    full_name: event.target.value,
                  }))
                }
                placeholder="Prénom Nom"
                required
              />
            </PilotageField>
            <PilotageField label="Email" htmlFor="collaborator-email">
              <Input
                id="collaborator-email"
                type="email"
                value={formValues.email}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    email: event.target.value,
                  }))
                }
                placeholder="consultant@lenvia.eu"
                required
              />
            </PilotageField>
            <PilotageField label="Statut" htmlFor="collaborator-status">
              <select
                id="collaborator-status"
                value={formValues.status}
                onChange={(event) => {
                  const nextStatus = event.target.value;
                  setFormValues((current) => {
                    const nextValues = { ...current, status: nextStatus };
                    if (["ACTIVE", "RECRUITMENT", "PLANNED"].includes(nextStatus)) {
                      nextValues.employment_end_date = "";
                    }
                    return nextValues;
                  });
                }}
                className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                {COLLABORATOR_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {getCollaboratorStatusLabel(status)}
                  </option>
                ))}
              </select>
            </PilotageField>
            <PilotageField label="Type de contrat" htmlFor="collaborator-contract-kind">
              <select
                id="collaborator-contract-kind"
                value={formValues.contract_kind}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    contract_kind: event.target.value,
                  }))
                }
                className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue"
              >
                {CONTRACT_KINDS.map((contractKind) => (
                  <option key={contractKind} value={contractKind}>
                    {getContractKindLabel(contractKind)}
                  </option>
                ))}
              </select>
            </PilotageField>
            <PilotageField label="Date d'entrée" htmlFor="collaborator-start-date">
              <Input
                id="collaborator-start-date"
                type="date"
                value={formValues.employment_start_date}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    employment_start_date: event.target.value,
                  }))
                }
                required
              />
            </PilotageField>
            <PilotageField label="Date de sortie" htmlFor="collaborator-end-date">
              <Input
                id="collaborator-end-date"
                type="date"
                value={formValues.employment_end_date}
                onChange={(event) =>
                  setFormValues((current) => ({
                    ...current,
                    employment_end_date: event.target.value,
                  }))
                }
                disabled={["ACTIVE", "RECRUITMENT", "PLANNED"].includes(formValues.status)}
                required={["LEFT", "INACTIVE"].includes(formValues.status)}
              />
            </PilotageField>

            {isSalariedContract ? (
              <div
                data-testid="collaborator-salary-section"
                className="col-span-full rounded-2xl border border-white/10 bg-white/[0.02] p-4 space-y-4"
              >
                <div className="flex items-start gap-2.5">
                  <Info className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                  <div>
                    <h4 className="text-sm font-semibold text-white">
                      Rémunération & coût employeur
                    </h4>
                    <p className="mt-0.5 text-xs text-slate-400">
                      Ces informations décrivent la rémunération du salarié chez LENVIA et constituent la référence unique pour les missions et les coûts employeur.
                    </p>
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-3">
                  <PilotageField label="Salaire brut annuel (€)" htmlFor="collaborator-annual-salary">
                    <Input
                      id="collaborator-annual-salary"
                      data-testid="collaborator-annual-salary-input"
                      type="number"
                      min="0"
                      step="any"
                      placeholder="ex. 50000"
                      value={formValues.annual_salary}
                      onChange={(event) =>
                        setFormValues((current) => ({
                          ...current,
                          annual_salary: event.target.value,
                        }))
                      }
                    />
                  </PilotageField>

                  <PilotageField label="Date d'effet initiale" htmlFor="collaborator-salary-effective-date">
                    <Input
                      id="collaborator-salary-effective-date"
                      data-testid="collaborator-salary-effective-date-input"
                      type="date"
                      value={formValues.salary_effective_date || formValues.employment_start_date}
                      onChange={(event) =>
                        setFormValues((current) => ({
                          ...current,
                          salary_effective_date: event.target.value,
                        }))
                      }
                    />
                  </PilotageField>

                  <PilotageField label="Coefficient de coût employeur" htmlFor="collaborator-employer-cost-coefficient">
                    <Input
                      id="collaborator-employer-cost-coefficient"
                      data-testid="collaborator-employer-cost-coefficient-input"
                      type="number"
                      min="0.1"
                      max="10"
                      step="0.01"
                      placeholder="1.5"
                      value={formValues.employer_cost_coefficient}
                      onChange={(event) =>
                        setFormValues((current) => ({
                          ...current,
                          employer_cost_coefficient: event.target.value,
                        }))
                      }
                    />
                  </PilotageField>
                </div>

                <div className="grid gap-3 sm:grid-cols-3 pt-2 border-t border-white/5">
                  <div className="rounded-xl border border-white/5 bg-slate-950/60 p-3">
                    <p className="text-[11px] font-medium text-slate-400">
                      Coût employeur annuel estimé
                    </p>
                    <p
                      data-testid="collaborator-estimated-annual-cost"
                      className="mt-1 text-base font-semibold text-white"
                    >
                      {formatEUR0(estimatedAnnualCost)}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Salaire annuel × coefficient ({employerCoeffNum})
                    </p>
                  </div>

                  <div className="rounded-xl border border-white/5 bg-slate-950/60 p-3">
                    <p className="text-[11px] font-medium text-slate-400">
                      Coût employeur mensuel estimé
                    </p>
                    <p
                      data-testid="collaborator-estimated-monthly-cost"
                      className="mt-1 text-base font-semibold text-white"
                    >
                      {formatEUR0(estimatedMonthlyCost)}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Coût annuel / 12 (lissé)
                    </p>
                  </div>

                  <div className="rounded-xl border border-white/5 bg-slate-950/60 p-3">
                    <p className="text-[11px] font-medium text-slate-400">
                      CJM calculé (base 210 j)
                    </p>
                    <p
                      data-testid="collaborator-calculated-cjm"
                      className="mt-1 text-base font-semibold text-white"
                    >
                      {formatEUR2(calculatedCjm)}
                    </p>
                    <p className="text-[10px] text-slate-500 mt-0.5">
                      Coût annuel / 210 j (indicatif mission)
                    </p>
                  </div>
                </div>

                {/* Accordéon Évolutions salariales */}
                <div className="pt-3 border-t border-white/5 space-y-3">
                  <div className="flex items-center justify-between">
                    <button
                      type="button"
                      data-testid="toggle-salary-changes-accordion"
                      onClick={() => setIsSalaryChangesOpen((open) => !open)}
                      className="flex items-center gap-2 text-xs font-semibold text-slate-200 hover:text-white transition-colors"
                    >
                      {isSalaryChangesOpen ? (
                        <ChevronDown className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-slate-400" />
                      )}
                      <TrendingUp className="h-3.5 w-3.5 text-emerald-400" />
                      <span>Évolutions salariales</span>
                      <span className="rounded-full bg-white/10 px-2 py-0.5 text-[10px] font-mono text-slate-300">
                        {(formValues.salary_changes || []).length}
                      </span>
                    </button>

                    {isSalaryChangesOpen && editingChangeIndex === null ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        data-testid="add-salary-change-btn"
                        onClick={handleOpenAddEvolution}
                        className="h-7 text-xs border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Programmer une évolution
                      </Button>
                    ) : null}
                  </div>

                  {isSalaryChangesOpen ? (
                    <div className="space-y-3 rounded-xl border border-white/5 bg-slate-950/40 p-3">
                      {/* Sous-formulaire d'ajout / modification */}
                      {editingChangeIndex !== null ? (
                        <div
                          data-testid="evolution-subform"
                          className="rounded-lg border border-emerald-500/20 bg-emerald-950/10 p-3 space-y-3"
                        >
                          <div className="flex items-center justify-between">
                            <h5 className="text-xs font-semibold text-emerald-300">
                              {editingChangeIndex === "new"
                                ? "Nouvelle évolution salariale"
                                : "Modifier l'évolution salariale"}
                            </h5>
                            <button
                              type="button"
                              onClick={handleCancelEvolution}
                              className="text-slate-400 hover:text-white text-xs"
                            >
                              Annuler
                            </button>
                          </div>

                          {evolutionError ? (
                            <p
                              data-testid="evolution-error"
                              className="text-xs text-red-400 bg-red-950/30 border border-red-500/20 rounded p-1.5"
                            >
                              {evolutionError}
                            </p>
                          ) : null}

                          <div className="grid gap-3 sm:grid-cols-2">
                            <div>
                              <label
                                htmlFor="evolution-effective-date"
                                className="block text-[11px] font-medium text-slate-300 mb-1"
                              >
                                Date d'effet
                              </label>
                              <Input
                                id="evolution-effective-date"
                                data-testid="evolution-effective-date-input"
                                type="date"
                                value={evolutionForm.effective_date}
                                onChange={(e) =>
                                  setEvolutionForm((curr) => ({
                                    ...curr,
                                    effective_date: e.target.value,
                                  }))
                                }
                              />
                            </div>

                            <div>
                              <label
                                htmlFor="evolution-value"
                                className="block text-[11px] font-medium text-slate-300 mb-1"
                              >
                                Nouveau salaire brut annuel total (€)
                              </label>
                              <Input
                                id="evolution-value"
                                data-testid="evolution-value-input"
                                type="number"
                                min="0"
                                step="any"
                                placeholder="ex. 53000"
                                value={evolutionForm.value}
                                onChange={(e) =>
                                  setEvolutionForm((curr) => ({
                                    ...curr,
                                    value: e.target.value,
                                  }))
                                }
                              />
                            </div>

                            <div className="sm:col-span-2">
                              <label
                                htmlFor="evolution-comment"
                                className="block text-[11px] font-medium text-slate-300 mb-1"
                              >
                                Commentaire / Motif (optionnel)
                              </label>
                              <Input
                                id="evolution-comment"
                                data-testid="evolution-comment-input"
                                type="text"
                                placeholder="ex. Revalorisation annuelle"
                                value={evolutionForm.comment}
                                onChange={(e) =>
                                  setEvolutionForm((curr) => ({
                                    ...curr,
                                    comment: e.target.value,
                                  }))
                                }
                              />
                            </div>
                          </div>

                          <div className="flex justify-end gap-2 pt-1">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              data-testid="cancel-evolution-btn"
                              onClick={handleCancelEvolution}
                              className="h-7 text-xs border-white/10 text-slate-300 hover:bg-white/5"
                            >
                              Annuler
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              data-testid="save-evolution-btn"
                              onClick={handleSaveEvolution}
                              className="h-7 text-xs bg-emerald-600 hover:bg-emerald-500 text-white"
                            >
                              {editingChangeIndex === "new" ? "Ajouter l'évolution" : "Valider la modification"}
                            </Button>
                          </div>
                        </div>
                      ) : null}

                      {/* Liste des évolutions */}
                      {(formValues.salary_changes || []).length === 0 && editingChangeIndex === null ? (
                        <p className="text-xs text-slate-400 py-2 text-center">
                          Aucune évolution salariale enregistrée.
                        </p>
                      ) : (
                        <div className="space-y-2">
                          {(formValues.salary_changes || []).map((change, idx) => {
                            const isApplied = toInputDate(change.effective_date) <= todayStr;
                            const prevSalary =
                              idx === 0
                                ? annualSalaryNum
                                : Number(formValues.salary_changes[idx - 1]?.value || 0);
                            const changeVal = Number(change.value || 0);
                            const deltaEur = changeVal - prevSalary;
                            const deltaPct =
                              prevSalary > 0
                                ? ((changeVal - prevSalary) / prevSalary) * 100
                                : null;

                            return (
                              <div
                                key={change.id || idx}
                                data-testid={`salary-change-row-${idx}`}
                                className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-lg border border-white/5 bg-slate-900/60 p-2.5 text-xs text-slate-200"
                              >
                                <div className="space-y-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-semibold text-white">
                                      {formatDateLabel(change.effective_date)}
                                    </span>
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                        isApplied
                                          ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/30"
                                          : "bg-sky-500/20 text-sky-300 border border-sky-500/30"
                                      }`}
                                    >
                                      {isApplied ? "Appliquée" : "Programmée"}
                                    </span>
                                  </div>

                                  <div className="flex flex-wrap items-center gap-x-3 text-[11px] text-slate-400">
                                    <span>
                                      Précédent :{" "}
                                      <span className="text-slate-300">
                                        {formatEUR0(prevSalary)}
                                      </span>
                                    </span>
                                    <span>
                                      Nouveau :{" "}
                                      <span className="font-semibold text-emerald-400">
                                        {formatEUR0(changeVal)}
                                      </span>
                                    </span>
                                    <span
                                      className={`font-mono font-medium ${
                                        deltaEur > 0
                                          ? "text-emerald-400"
                                          : deltaEur < 0
                                          ? "text-red-400"
                                          : "text-slate-400"
                                      }`}
                                    >
                                      {deltaEur > 0 ? `+${formatEUR0(deltaEur)}` : formatEUR0(deltaEur)}
                                      {deltaPct !== null
                                        ? ` (${deltaPct > 0 ? `+${deltaPct.toFixed(1)}%` : `${deltaPct.toFixed(1)}%`})`
                                        : ""}
                                    </span>
                                  </div>

                                  {change.comment ? (
                                    <p className="text-[10px] text-slate-400 italic">
                                      {change.comment}
                                    </p>
                                  ) : null}

                                  {change.created_by ? (
                                    <p className="text-[10px] text-slate-500">
                                      Par {change.created_by}
                                    </p>
                                  ) : null}
                                </div>

                                <div className="flex items-center gap-1 self-end sm:self-center">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`edit-salary-change-btn-${idx}`}
                                    onClick={() => handleEditEvolution(idx)}
                                    className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                                    title="Modifier"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    data-testid={`delete-salary-change-btn-${idx}`}
                                    onClick={() => handleDeleteEvolution(idx)}
                                    className="h-7 w-7 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}
          </div>
        </PilotageModal>
      ) : null}
    </div>
  );
};

export default CollaboratorsPage;
